import axios, { AxiosRequestConfig, AxiosResponse, AxiosError, AxiosInstance, AxiosHeaders } from 'axios';
import { msalInstance } from '..';
import { loginRequest } from './authConfig';
import { PuppeteerErrorHandler } from './errorParsers/PuppeteerErrorParser';
import { CorrelationUtils } from './correlationUtils';
import { showErrorNotificationAsync } from './messageUtils';
import { ApiErrorHandler } from './apiTypes';

const pdfServerUrl = process.env.REACT_APP_PDF_GENERATOR_URL;
if (!pdfServerUrl) throw new Error("REACT_APP_PDF_GENERATOR_URL not set");

// Create a dedicated axios instance for the Puppeteer/Node.js server
const puppeteerAxios: AxiosInstance = axios.create({
    baseURL: pdfServerUrl,
    timeout: 60000, // 60 seconds timeout for PDF generation
});

/**
 * Sets up request interceptor to add correlation ID and auth headers
 */
puppeteerAxios.interceptors.request.use(
    async (config) => {
        // Ensure headers object exists (axios 1.x uses AxiosHeaders)
        if (!config.headers) {
            config.headers = new AxiosHeaders();
        }
        
        // Add correlation ID header if not already present
        if (!config.headers['x-correlation-id']) {
            const correlationId = CorrelationUtils.generateCorrelationId();
            config.headers['x-correlation-id'] = correlationId;
        }
        
        // Add authentication token
        const account = msalInstance.getActiveAccount();
        if (account) {
            try {
                const tokenResponse = await msalInstance.acquireTokenSilent({
                    ...loginRequest,
                    account: account
                });
                config.headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
            } catch (error) {
                console.error('Failed to acquire token for Puppeteer request:', error);
            }
        }
        
        return config;
    },
    (error) => {
        console.error('Puppeteer request interceptor error:', error);
        return Promise.reject(error);
    }
);

/**
 * Sets up response interceptor for centralized error handling
 */
puppeteerAxios.interceptors.response.use(
    (response: AxiosResponse) => {        
        return response;
    },
    async (error: AxiosError) => {
        if (ApiErrorHandler.isCancelledRequest(error)) {
            return Promise.reject(error);
        }

        const requestMethod = (error.config?.method || 'GET').toUpperCase();
        const requestPath = error.config?.url && error.config.url.trim() ? error.config.url : '/';

        // Parse error with Puppeteer-specific handler
        const apiError = await PuppeteerErrorHandler.parseError(error, pdfServerUrl);
        const requestTarget = apiError.url && apiError.url.trim() ? apiError.url : requestPath;
        
        // Log error details
        console.error('Puppeteer API Error:', {
            url: apiError.url,
            statusCode: apiError.statusCode,
            message: apiError.message,
            correlationId: apiError.correlationId,
            timestamp: apiError.timestamp,
            backendSource: apiError.backendSource
        });
        
        // Show error notification
        let errorTitle = 'Serverfel';
        let errorDescription: string | undefined;
        switch (apiError.statusCode) {
            case 400:
                errorTitle = 'Felaktig förfrågan';
                break;
            case 401:
                errorTitle = 'Autentiseringsfel';
                break;
            case 403:
                errorTitle = 'Åtkomst nekad';
                break;
            case 404:
                errorTitle = 'Resurs hittades inte';
                break;
            case 422:
                errorTitle = 'Valideringsfel';
                break;
            case 500:
            case 502:
            case 503:
                errorTitle = 'Serverfel';
                break;
            case 0:
                errorTitle = 'Nätverksfel';
                errorDescription = `Kunde inte nå Puppeteer PDF-service. Misslyckat anrop: ${requestMethod} ${requestTarget}. Kontrollera att tjänsten är igång.`;
                break;
            default:
                errorTitle = 'Ett fel uppstod';
        }
        
        await showErrorNotificationAsync(
            errorTitle,
            apiError,
            {
                description: errorDescription,
                duration: 0, // Persistent until closed
                showDetails: true
            }
        );
        
        // Reject with the standardized error format
        return Promise.reject(apiError);
    }
);

/**
 * PuppeteerApiClient
 * 
 * A client to make requests to the Puppeteer/Node.js PDF generation server
 * with standardized error handling
 */
export class PuppeteerApiClient {
    /**
     * Perform a POST request to the Puppeteer server
     * @param url - The endpoint path (relative to baseURL)
     * @param data - The data to send
     * @param config - Additional axios configuration
     * @returns Promise with response
     */
    public static async post<T = any>(
        url: string, 
        data = {}, 
        config?: AxiosRequestConfig<any> | undefined
    ): Promise<AxiosResponse<T>> {
        return await puppeteerAxios.post<T>(url, data, config);
    }

    /**
     * Perform a POST request with blob response (for PDF downloads)
     * @param url - The endpoint path (relative to baseURL)
     * @param data - The data to send
     * @param config - Additional axios configuration
     * @returns Promise with blob response
     */
    public static async postBlob<T = Blob>(
        url: string, 
        data = {}, 
        config?: AxiosRequestConfig<any> | undefined
    ): Promise<AxiosResponse<T>> {
        return await puppeteerAxios.post<T>(url, data, {
            ...config,
            responseType: 'blob'
        });
    }
}
